import { type UseQueryResult, useQueries } from "@tanstack/react-query"
import type { Omit } from "@wagmi/core/internal"
import { hashFn } from "@wagmi/core/query"
import { useMemo } from "react"
import { Evaluate } from "viem"
import { type Config, useChainId, useConfig } from "wagmi"
import {
  type GetPerspectiveErrorType,
  getPerspective,
} from "../actions/getPerspective"
import {
  type GetPerspectiveData,
  type GetPerspectiveOptions,
  type GetPerspectiveQueryFnData,
  type GetPerspectiveQueryKey,
  getPerspectiveQueryKey,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
} from "../types"

export type UsePerspectivesProps<
  config extends Config = Config,
  selectedData = GetPerspectiveData,
  combineData = UseQueryResult<selectedData>[],
> = ConfigParameter<config> & {
  queries: readonly (GetPerspectiveOptions & {
    query?:
      | Evaluate<
          Omit<
            UseQueryParameters<
              GetPerspectiveQueryFnData,
              GetPerspectiveErrorType,
              GetPerspectiveData,
              GetPerspectiveQueryKey
            >,
            "gcTime" | "staleTime" | "select"
          >
        >
      | undefined
  })[]
  select?: (data: GetPerspectiveData) => selectedData
  combine?: (result: UseQueryResult<selectedData>[]) => combineData
}

export function usePerspectives<
  config extends ExtendedConfig,
  selectedData = GetPerspectiveData,
  combineData = UseQueryResult<selectedData>[],
>(
  parameters: UsePerspectivesProps<config, selectedData, combineData>,
): combineData {
  const { queries = [], select, combine } = parameters
  const config = useConfig(parameters)
  const chainId = useChainId({ config })
  const queryResults = useQueries({
    queries: queries.map(({ scopeKey, query = {}, ...perspective }) => ({
      ...query,
      queryKey: getPerspectiveQueryKey({
        address: perspective.address,
        id: perspective.id,
        chainId: perspective.chainId || chainId,
        evcAddress: perspective.evcAddress,
        scopeKey,
      }),
      queryKeyHashFn: hashFn,
      queryFn: () =>
        getPerspective(config, {
          ...perspective,
          chainId: perspective.chainId || chainId,
        }),
      select: select,
      refetchOnMount: false,
      retry: false,
      enabled: Boolean(perspective.address && (query.enabled ?? true)),
    })),
  }) satisfies UseQueryResult<selectedData>[]

  queryResults.some((v) => {
    if (v.isError) {
      console.error("[ERROR][usePerspectives] queryResults", v)
    }
  })

  return useMemo(
    () =>
      combine
        ? combine(queryResults)
        : (queryResults as unknown as combineData),
    [queryResults, combine],
  )
}
